import React, { useEffect, useState } from 'react';
import {
  getGameSessions,
  GameSession,
} from '../../services/getGameSessions.service';
import { TableLoading } from '../Loading';
import styles from '../Transactions/TransactionsPage.module.css';
import { Pagination, PaginationItem, Stack } from '@mui/material';
import { ReactComponent as SortIcon } from '../../assets/icons/sort.svg';


type Sorting = 'desc' | 'asc' | 'def';

const GameHistoryPage: React.FC = () => {
  const PAGE_SIZE = 10;
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [sortedGameSessions, setSortedGameSessions] = useState<GameSession[]>([]);

  const [sortingByBalanceStart, setSortingByBalanceStart] = useState<Sorting>('def');
  const [sortingByBalanceEnd, setSortingByBalanceEnd] = useState<Sorting>('def');
  useEffect(() => {
    setSortedGameSessions(sortByBalansStartedData(sortingByBalanceStart));
  }, [sortingByBalanceStart, sessions]);

  useEffect(() => {
    setSortedGameSessions(sortByBalansEndData(sortingByBalanceEnd));
  }, [sortingByBalanceEnd, sessions]);


  const sortByBalansStartedData = (sorting: Sorting) => {
    if (sorting === 'def') return sessions;
    return sorting === 'desc' ? [...sessions].sort((a, b) => Number(b?.balanceStart) - Number(a?.balanceStart)) : [...sessions].sort((a, b) => Number(a?.balanceStart) - Number(b?.balanceStart));
  };

  const sortByBalansEndData = (sorting: Sorting) => {
    if (sorting === 'def') return sessions;
    return sorting === 'desc' ? [...sessions].sort((a, b) => Number(b?.balanceEnd) - Number(a?.balanceEnd)) : [...sessions].sort((a, b) => Number(a?.balanceEnd) - Number(b?.balanceEnd));
  };

  useEffect(() => {
    setLoading(true);
    getGameSessions({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      startDate: dateFrom || undefined,
      endDate: dateTo || undefined,
    })
      .then((data) => {
        setSessions(data.sessions);
        setTotal(data.total || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch game sessions:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, searchTrigger]);

  const [siblingCount, setSiblingCount] = useState(2);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 890) {
        setSiblingCount(0); // mobile: only show current page
      } else if (window.innerWidth < 1080) {
        setSiblingCount(1); // tablet: show 1 on each side
      } else {
        setSiblingCount(2); // desktop: show 2 on each side
      }
    };

    handleResize(); // run once
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  return (
    <main className={styles.main}>
      <h1 className={styles.title}>Game History</h1>
      {/* Search by Date Section */}
      <div className={styles.searchByDate}>
        <label className={styles.label} style={{ minWidth: 120 }}>
          Search by Date
        </label>
        <div className={styles.dateInputGroup}>
          <span className={styles.dateIcon}>📅</span>
          <input
            type="date"
            className={styles.dateInput}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="Start Date"
          />
          <span className={styles.toText}>To</span>
          <input
            type="date"
            className={styles.dateInput}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="Finish Date"
          />
        </div>
        <button className={styles.searchBtn} onClick={handleSearch}>
          Search
        </button>
      </div>
      <TableLoading
        loading={loading}
        rows={8}
        columns={11}
        text="Loading game sessions..."
        className={styles.tableWrapper}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Game Name</th>
                <th>User</th>
                <th>Game ID</th>
                <th>Provider ID</th>
                <th>Session Token</th>
                <th>Demo</th>
                <th>Status</th>
                <th>
                  <div onClick={() => setSortingByBalanceStart(() => {
                    if (sortingByBalanceStart === 'asc') return 'desc';
                    if (sortingByBalanceStart === 'desc') return 'asc';
                    return 'asc';
                  })} className={styles.sortable}>Balance Start <div className={styles.sortIcon}><SortIcon /></div></div>
                </th>
                <th>
                  <div onClick={() => setSortingByBalanceEnd(() => {
                    if (sortingByBalanceEnd === 'asc') return 'desc';
                    if (sortingByBalanceEnd === 'desc') return 'asc';
                    return 'asc';
                  })} className={styles.sortable}>Balance End <div className={styles.sortIcon}><SortIcon /></div></div>
                </th>
                <th>IP Address</th>
                <th>Created At</th>
                <th>Ended At</th>
              </tr>
            </thead>
            <tbody>
              {sortedGameSessions.map((session) => (
                <tr key={session.id}>
                  <td>{session.gameName}</td>
                  <td>{session.username || session.userId}</td>
                  <td>{session.gameId}</td>
                  <td>{session.providerId}</td>
                  <td>{session.sessionToken}</td>
                  <td>{session.isDemo ? 'Yes' : 'No'}</td>
                  <td>{session.status}</td>
                  <td>{session.balanceStart}</td>
                  <td>{session.balanceEnd ?? '-'}</td>
                  <td>{session.ipAddress || '-'}</td>
                  <td>
                    {session.createdAt
                      ? new Date(session.createdAt).toLocaleString()
                      : '-'}
                  </td>
                  <td>
                    {session.endedAt
                      ? new Date(session.endedAt).toLocaleString()
                      : '-'}
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && !loading && (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center' }}>
                    No game sessions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination controls */}
        <div className={styles.paginationControls}>
          {(total && Math.ceil(total / PAGE_SIZE) > 0) ?
            <Stack spacing={1}>
              <Pagination
                onChange={(e, value) => setPage(value)}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: () => (
                        <button
                          onClick={() => {
                            if (page > 1) setPage((p) => p - 1);
                          }}
                          disabled={page === 1}
                          className={styles.searchBtn + " " + styles.paginationBtn}
                        >
                          Prev
                        </button>
                      ),
                      next: () => (
                        <button
                          onClick={() => {
                            if (page < Math.ceil(total / PAGE_SIZE)) setPage((p) => p + 1);
                          }}
                          disabled={page >= Math.ceil(total / PAGE_SIZE)}
                          className={styles.searchBtn + " " + styles.paginationBtn}
                        >
                          Next
                        </button>
                      ),
                    }}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "#313485",
                        color: "#fff",
                        padding: "5px",
                        "&:hover": { backgroundColor: "#232665" }, // darker hover
                      },
                      // "&:not(.Mui-selected)": {
                      //   border: "1px solid #313485",
                      //   color: "#313485",
                      // },
                      // borderRadius: "8px", // optional rounding
                      padding: "5px",
                    }}
                  />
                )}
                count={Math.ceil(total / PAGE_SIZE)}
                page={page}
                color="primary"
                size="medium"
                shape="rounded"
                siblingCount={siblingCount}
                boundaryCount={1}
              />
            </Stack>
            :
            <button
              disabled={page === 1}
              className={styles.searchBtn + ' ' + styles.paginationBtn}
            >
              1
            </button>
          }
        </div>
      </TableLoading>
    </main>
  );
};

export default GameHistoryPage;
